import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  Table,
  Tag,
  Select,
  Input,
  Button,
  Tooltip,
  Space,
  Typography,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import css from "./QuizResponse.module.css";
import { quizResponseActions } from "../../../store/slices/messages-slices";
import { packCategoryService } from "../../../services/settings-services/packCategory.service";
import { siteConfigService } from "../../../services/builder-services/builder.service";
import { resolveTranslatableValue } from "../../builderSection/ClientBuilderPanel/translatable.util";
import { QuizResponseDetailsModal } from "./QuizResponseDetailsModal/QuizResponseDetailsModal";

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending", color: "orange" },
  { value: "CONTACTED", label: "Contacted", color: "blue" },
  { value: "COMPLETED", label: "Completed", color: "green" },
  { value: "CANCELLED", label: "Cancelled", color: "red" },
];

const statusColor = (status) => {
  const found = STATUS_OPTIONS.find((s) => s.value === status);
  return found ? found.color : "default";
};

// Old-shape rows (no `answers` map) populate these fixed fields instead —
// used only to compute the "N / M answered" summary count for such rows.
const LEGACY_FIELD_KEYS = [
  "primaryGoal",
  "dietaryFocus",
  "lifestyle",
  "activityLevel",
  "caloriePreference",
  "excludedMeats",
  "medicalConditions",
];

function QuizResponse() {
  const dispatch = useDispatch();
  const { data, total, status } = useSelector((store) => store.quizResponse);

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [updatingIds, setUpdatingIds] = useState([]);
  const [query, setQuery] = useState({ page: 1, limit: 15, createdAt: "desc" });
  const [packCategoriesById, setPackCategoriesById] = useState({});
  const [contactExtraFieldsById, setContactExtraFieldsById] = useState({});
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  useEffect(() => {
    packCategoryService.getAllLite()
      .then((categories) => {
        setPackCategoriesById(
          Object.fromEntries(
            (categories || []).map((c) => [c.id, resolveTranslatableValue(c.name, "en") || c.id])
          )
        );
      })
      .catch(() => setPackCategoriesById({}));
  }, []);

  // Resolves extraContactFields ids (Instagram/Telegram/etc.) to their
  // currently-published labels — same "id -> readable label" lookup
  // pattern as packCategoriesById above. Falls back to the raw id in the
  // modal if a field was since deleted/renamed, same convention as
  // answersSnapshot's question-id fallback.
  useEffect(() => {
    siteConfigService.getAllSections()
      .then((sections) => {
        const quizData = sections?.find((s) => s.key === "quiz")?.data;
        const layoutProps = quizData?.byLayout?.[quizData?.layout];
        setContactExtraFieldsById(
          Object.fromEntries(
            (layoutProps?.contactExtraFields || []).map((f) => [f.id, resolveTranslatableValue(f.label, "en") || f.id])
          )
        );
      })
      .catch(() => setContactExtraFieldsById({}));
  }, []);

  useEffect(() => {
    dispatch(quizResponseActions.getQuizResponses(query));
  }, [dispatch, query]);

  // Same "prefer the frozen snapshot, fall back to resolving raw ids
  // against the current config" rule as QuizResponseDetailsModal — a
  // deleted/renamed field still shows its real submission-time label here
  // instead of a raw id, for any row from before the snapshot existed.
  const getExtraContactFieldEntries = (record) => {
    if (Array.isArray(record.extraContactFieldsSnapshot) && record.extraContactFieldsSnapshot.length > 0) {
      return record.extraContactFieldsSnapshot.map((entry) => ({ label: entry.label, value: entry.value }));
    }
    return Object.entries(record.extraContactFields || {})
      .filter(([, value]) => !!value)
      .map(([fieldId, value]) => ({ label: contactExtraFieldsById[fieldId] || fieldId, value }));
  };

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingIds((prev) => [...prev, id]);
    try {
      unwrapResult(
        await dispatch(
          quizResponseActions.updateQuizResponse({
            id,
            data: { status: newStatus },
          }),
        ),
      );
      toast.success("Status updated successfully");
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdatingIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const startEditNote = (record) => {
    setEditingNoteId(record.id);
    setNoteValue(record.note || "");
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setNoteValue("");
  };

  const saveNote = async (id) => {
    setUpdatingIds((prev) => [...prev, id]);
    try {
      unwrapResult(
        await dispatch(
          quizResponseActions.updateQuizResponse({
            id,
            data: { note: noteValue },
          }),
        ),
      );
      toast.success("Note saved successfully");
      setEditingNoteId(null);
      setNoteValue("");
    } catch {
      toast.error("Failed to save note");
    } finally {
      setUpdatingIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const columns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      width: 160,
    },
    {
      title: "Contact",
      key: "contact",
      width: 220,
      render: (_, record) => (
        <div className={css.contactCell}>
          <Text className={css.email}>{record.email}</Text>
          <Text type="secondary" className={css.phone}>
            {record.phone}
          </Text>
          {getExtraContactFieldEntries(record).map(({ label, value }) => (
            <Text type="secondary" key={label} style={{ display: "block", fontSize: 11 }}>
              {label}: {value}
            </Text>
          ))}
        </div>
      ),
    },
    {
      // Quiz questions/answers are fully dynamic (admin-editable via the
      // Builder), so this can't be fixed per-question columns — instead
      // shows a summary count and opens a modal listing the questions this
      // record actually saw. This NEVER compares a row against today's live
      // Builder config — that's what produced impossible counts like
      // "9 / 8 answered" once the admin edited the quiz after a submission.
      // Each row is judged purely by what IT has:
      //   - a snapshot -> both numbers came from the same frozen moment
      //   - raw `answers` only (pre-fix row) -> we only know how many it
      //     answered, not how many questions existed back then, so no
      //     denominator is shown at all rather than a fabricated/wrong one
      //   - neither -> true old-shape row, compared against the fixed
      //     legacy field count, which never changes
      title: "Answers",
      key: "answers",
      width: 160,
      render: (_, record) => {
        const hasSnapshot = Array.isArray(record.answersSnapshot) && record.answersSnapshot.length > 0;
        const isLegacyShape = !hasSnapshot && !record.answers;

        let label;
        if (hasSnapshot) {
          const total = record.answersSnapshot.length;
          const answered = record.answersSnapshot.filter((q) => q.answered).length;
          label = `${answered} / ${total} answered`;
        } else if (isLegacyShape) {
          const total = LEGACY_FIELD_KEYS.length;
          const answered = LEGACY_FIELD_KEYS.filter((k) => {
            const val = record[k];
            return Array.isArray(val) ? val.length > 0 : !!val;
          }).length;
          label = `${answered} / ${total} answered`;
        } else {
          label = `${Object.keys(record.answers).length} answered`;
        }

        return (
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRecord(record);
              setIsDetailsOpen(true);
            }}
          >
            {label}
          </Button>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (statusVal, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            value={statusVal}
            size="small"
            loading={updatingIds.includes(record.id)}
            onChange={(val) => handleStatusChange(record.id, val)}
            className={css.statusSelect}
            options={STATUS_OPTIONS.map((s) => ({
              value: s.value,
              label: (
                <Tag color={s.color} className={css.statusTag}>
                  {s.label}
                </Tag>
              ),
            }))}
          />
        </div>
      ),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      width: 220,
      render: (note, record) =>
        editingNoteId === record.id ? (
          <div className={css.noteEditCell} onClick={(e) => e.stopPropagation()}>
            <TextArea
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              className={css.noteTextarea}
            />
            <Space className={css.noteActions}>
              <Tooltip title="Save">
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  loading={updatingIds.includes(record.id)}
                  onClick={() => saveNote(record.id)}
                />
              </Tooltip>
              <Tooltip title="Cancel">
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={cancelEditNote}
                />
              </Tooltip>
            </Space>
          </div>
        ) : (
          <div className={css.noteViewCell} onClick={(e) => e.stopPropagation()}>
            <Text className={css.noteText}>
              {note || <span className={css.emptyNote}>No note</span>}
            </Text>
            <Tooltip title="Edit note">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                className={css.editNoteBtn}
                onClick={() => startEditNote(record)}
              />
            </Tooltip>
          </div>
        ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      sorter: true,
      sortOrder: query.createdAt === "asc" ? "ascend" : "descend",
      render: (date) => dayjs(date).format("DD.MM.YYYY HH:mm"),
    },
  ];

  return (
    <section className={css.wrap}>
      <Table
        rowKey="id"
        dataSource={data}
        columns={columns}
        loading={status === "loading"}
        onChange={(newPage, newLimit, sorter, extra) => {
          console.log(newPage, newLimit, sorter, extra);
          setQuery((prev) => ({
            page: newPage.current,
            limit: newPage.pageSize,
            createdAt:
              sorter.field === "createdAt"
                ? sorter.order === "ascend"
                  ? "asc"
                  : "desc"
                : "asc",
          }));
        }}
        rowClassName={(record) =>
          updatingIds.includes(record.id) ? css.rowUpdating : ""
        }
        onRow={(record) => ({
          onClick: () => {
            setSelectedRecord(record);
            setIsDetailsOpen(true);
          },
        })}
        scroll={{ x: 1760 }}
        pagination={{
          current: query.page,
          pageSize: query.limit,
          total: total,
          showSizeChanger: true,
          showTotal: (t) => `Total: ${t}`,
          // onChange: handlePageChange,
        }}
        size="middle"
        bordered
      />
      <QuizResponseDetailsModal
        isOpen={isDetailsOpen}
        setIsOpen={setIsDetailsOpen}
        record={selectedRecord}
        packCategoriesById={packCategoriesById}
        contactExtraFieldsById={contactExtraFieldsById}
      />
    </section>
  );
}

export default QuizResponse;
